import React, { useMemo } from 'react';

import { Box, type BoxProps } from '@frontend/components';

import { CASCADER_SUBCOMPONENT_NAMES } from '../consts';
import { useCascaderState, useCascaderComponentsContext } from '../context';
import type { CascaderOption, CascaderCommonProps } from '../types';

export interface ControlProps<OptionType extends CascaderOption>
  extends BoxProps {
  children: React.ReactNode;
  getCommonProps: () => CascaderCommonProps<OptionType>;
}

export const InternalControl = <OptionType extends CascaderOption>({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { cascaderStyles, comboboxApi, getCommonProps } =
    useCascaderState<OptionType>();
  const { getComponent } = useCascaderComponentsContext<OptionType>();
  const CustomControl = useMemo(
    () => getComponent(CASCADER_SUBCOMPONENT_NAMES.CONTROL),
    [getComponent],
  );

  return (
    <CustomControl
      getCommonProps={getCommonProps}
      __css={cascaderStyles.control}
      {...comboboxApi.getControlProps()}
      className="cascader-control"
    >
      {children}
    </CustomControl>
  );
};

export const Control = <OptionType extends CascaderOption>({
  children,
  getCommonProps: _,
  ...rest
}: ControlProps<OptionType>) => {
  return <Box {...rest}>{children}</Box>;
};
